/**
 * The server-side recipe draft (one per season stage). The server owns every rule; these are plain calls.
 * Wire shapes mirror server/src/domain/recipeDraft/types.ts.
 */
import axios from 'axios';

export type StepName = 'WATER' | 'CROP' | 'SALTS' | 'RECIPE' | 'RESULTS';
export type StepStatus = 'NOT_STARTED' | 'DRAFT' | 'CHECKED' | 'CONFIRMED' | 'STALE';

export interface Finding {
  severity: 'blocker' | 'warning' | 'info';
  code: string;
  message: string;
  fix?: string;
  source: 'server' | 'client';
}

export interface StepView {
  status: StepStatus;
  inputs: unknown | null;
  inputsHash: string | null;
  findings: Finding[];
  checkedHash: string | null;
  warningsAcknowledged: boolean;
}

export interface DraftView {
  rev: number;
  stageOverridden: boolean;
  steps: Record<StepName, StepView>;
  stage: { id: string; name: string; formulationVersion: unknown };
}

export interface StageRef {
  projectId: string;
  seasonId: string;
  stageId: string;
}

/** The draft changed somewhere else since this copy was read. Nothing was saved; `draft` is the current one. */
export class DraftConflictError extends Error {
  draft: DraftView;
  constructor(draft: DraftView, message = 'This draft was changed in another window. Reload to continue.') {
    super(message);
    this.name = 'DraftConflictError';
    this.draft = draft;
  }
}

/** A rule refused the change (locked step, blocker, stale hash...). `draft` is the current one. */
export class DraftRuleError extends Error {
  code: string;
  draft: DraftView;
  constructor(code: string, message: string, draft: DraftView) {
    super(message);
    this.name = 'DraftRuleError';
    this.code = code;
    this.draft = draft;
  }
}

const base = (ref: StageRef) => `/api/projects/${ref.projectId}/seasons/${ref.seasonId}/stages/${ref.stageId}/draft`;

async function unwrap<T = DraftView>(request: Promise<{ data: { data: T } }>): Promise<T> {
  try {
    return (await request).data.data;
  } catch (err: any) {
    const body = err?.response?.data;
    if (body?.code === 'DRAFT_CONFLICT' && body.data) throw new DraftConflictError(body.data, body.error);
    if (typeof body?.code === 'string' && body.data) throw new DraftRuleError(body.code, body.error, body.data);
    throw err;
  }
}

export const recipeDraftApi = {
  get: (ref: StageRef) => unwrap(axios.get(base(ref))),

  putInputs: (ref: StageRef, step: StepName, rev: number, inputs: unknown) =>
    unwrap(axios.put(`${base(ref)}/steps/${step}`, { rev, inputs })),

  check: (ref: StageRef, step: StepName, rev: number, clientFindings?: Finding[]) =>
    unwrap(axios.post(`${base(ref)}/steps/${step}/check`, clientFindings?.length ? { rev, clientFindings } : { rev })),

  confirm: (ref: StageRef, step: StepName, rev: number, inputsHash: string, acknowledgeWarnings = false) =>
    unwrap(axios.post(`${base(ref)}/steps/${step}/confirm`, { rev, inputsHash, acknowledgeWarnings })),

  setStageOverride: (ref: StageRef, rev: number, overridden: boolean) =>
    unwrap(axios.put(`${base(ref)}/stage`, { rev, overridden })),

  /** Turns a fully confirmed draft into the immutable run. Asking again for the same state returns the same run. */
  issue: (ref: StageRef, rev: number) =>
    unwrap<{ runId: string; created: boolean }>(axios.post(`${base(ref)}/issue`, { rev })),
};
